import { useNavigate } from 'react-router';
import { OfficeDashboardTile } from '@tempmee/react-blocks';
import { SolidCircleDollarIcon } from '@tempmee/react-icons';
import { DiscountCodes } from '~/constants/ff.posthog';
import { deriveDiscountStatus, useGetDiscountCodes } from '~/domain/DiscountCode';
import { useIsViewingParentOffice } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { routes } from '~/routes';
import { pluralize } from '~/utils/String';
import { DASHBOARD_TILE_WIDTH } from '../dashboardTile';

/**
 * Dashboard "Active Promo Code" tile (GOTU-19168). Counts the office's discounts
 * — entered codes and codeless ones alike — whose chip on Payment Information
 * reads ACTIVE, and opens that page. The count reuses `deriveDiscountStatus`, so
 * the tile and the chips can never disagree.
 *
 * Renders nothing while the count is 0, while the list loads or has failed, with
 * the discount-codes flag off, and for a multi-location parent viewing its
 * overview: discounts belong to a location, so a parent has no count of its own.
 * That check is `useIsViewingParentOffice`, not `useParentOffice().isParentOffice`,
 * which reads true for every single-location office (its own id is the selected
 * one) and false for a DSO parent with no location picked — wrong both ways.
 *
 * Shares its query key with the Promo Codes section, so opening Payment
 * Information after the dashboard reads the list from cache. Skips the request
 * outright when the flag is off or a parent is viewing its overview. Rendered as
 * the DS `OfficeDashboardTile`, like the shift tiles beside it. No analytics
 * event is fired — the click is not tracked by decision.
 */
export const PromoCodesCard = () => {
  const navigate = useNavigate();
  const isEnabled = useFeatureFlag(DiscountCodes);
  const isViewingParentOffice = useIsViewingParentOffice();
  const { discountCodes, isLoading, isError } = useGetDiscountCodes({
    enabled: isEnabled && !isViewingParentOffice,
  });

  const activeCount = discountCodes.filter(
    (discountCode) => deriveDiscountStatus(discountCode) === 'active',
  ).length;

  if (!isEnabled || isViewingParentOffice || isLoading || isError || activeCount === 0) {
    return null;
  }

  return (
    <OfficeDashboardTile
      title={pluralize('Active Promo Code', activeCount)}
      description="View details"
      accent="cta"
      icon={SolidCircleDollarIcon}
      count={activeCount}
      onClick={() => navigate(routes.paymentInformation)}
      className={DASHBOARD_TILE_WIDTH}
      data-cy="promo-codes-card"
      data-count={activeCount}
    />
  );
};
